import React from "react";
import { Compass, Shuffle } from "lucide-react";
import { cn } from "../../../lib/utils";
import { WORKOUT_TYPE_OPTIONS } from "../../../config/workoutOptions";
import { SELECTOR_STYLES } from "../../../utils/mixedStyle";

const SELECTOR_COPY = {
  next: { Icon: Compass, line: "Fills the gaps in your week" },
  mixed: { Icon: Shuffle, line: "A different shape each time" },
};

const GOAL_OPTIONS = WORKOUT_TYPE_OPTIONS.filter(
  (o) => !SELECTOR_STYLES.includes(o.value),
);

/**
 * "Train for": the two ways to let Khronos choose, then every goal at once.
 * All options stay visible — no sideways scrolling — because this is the
 * screen's main decision, not an optional filter.
 */
const TrainForPicker = ({ value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const selectorOn = (v) =>
    selected.includes(v) || (v === "mixed" && selected.length === 0);

  const pickSelector = (v) => onChange(selected.includes(v) ? [] : [v]);
  const toggleGoal = (v) => {
    const goals = selected.filter((s) => !SELECTOR_STYLES.includes(s));
    onChange(goals.includes(v) ? goals.filter((g) => g !== v) : [...goals, v]);
  };

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        {["next", "mixed"].map((v) => {
          const { Icon, line } = SELECTOR_COPY[v];
          const on = selectorOn(v);
          const label = WORKOUT_TYPE_OPTIONS.find((o) => o.value === v)?.label;
          return (
            <button
              key={v}
              type="button"
              aria-pressed={on}
              onClick={() => pickSelector(v)}
              className={cn(
                "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-colors",
                on
                  ? "border-primary/50 bg-primary/10"
                  : "border-dashed border-border bg-card hover:border-primary/40",
              )}
            >
              <span
                className={cn(
                  "flex items-center gap-1.5 text-sm font-semibold",
                  on ? "text-primary" : "text-foreground",
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </span>
              <span className="text-xs leading-snug text-muted-foreground">
                {line}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground/80">
        <span className="h-px flex-1 bg-border" />
        or pick goals
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-1.5">
        {GOAL_OPTIONS.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              title={o.description}
              onClick={() => toggleGoal(o.value)}
              className={cn(
                "rounded-lg border px-2 py-2 text-[13px] transition-colors",
                on
                  ? "border-primary/50 bg-primary/10 font-semibold text-primary"
                  : "border-border bg-card text-foreground hover:border-primary/40",
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TrainForPicker;
